import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface VideoCardProps {
  article: Article;
  variant?: 'default' | 'compact' | 'featured';
}

export default function VideoCard({ article, variant = 'default' }: VideoCardProps) {
  const formatViews = (views: number): string => {
    if (views >= 10000) return `${(views / 10000).toFixed(1)}万`;
    return views.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-lg bg-background-100"
      >
        <div className="relative aspect-video overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - 久久视频高清在线观看`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <span className="inline-block px-2 py-0.5 text-xs bg-primary-500 text-background-50 rounded-md mb-2">
              {article.category}
            </span>
            <h3 className="text-lg md:text-xl font-heading text-white font-semibold leading-snug">
              {article.title}
            </h3>
            <p className="text-sm text-white/70 mt-1 line-clamp-1">{article.excerpt}</p>
            <div className="flex items-center gap-3 mt-2 text-xs text-white/60">
              <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(article.views)}</span>
              <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{formatViews(article.likes)}</span>
              <span>{article.publishDate}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="relative w-36 h-24 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            title={`${article.title} - 久久视频`}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <span className="absolute bottom-1 right-1 px-1.5 py-0.5 text-[10px] bg-black/70 text-white rounded">
            {article.duration}
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <div className="flex items-center gap-2 mt-1.5 text-xs text-foreground-500">
            <span className="bg-primary-50 text-primary-600 px-1.5 py-0.5 rounded">{article.category}</span>
            <span>{formatViews(article.views)}次观看</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 hover:bg-background-200/50 transition-colors"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          title={`${article.title} - 久久视频在线观看 ${article.category}`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        <span className="absolute bottom-2 right-2 px-2 py-1 text-xs bg-black/70 text-white rounded-md">
          {article.duration}
        </span>
        {article.isNew && (
          <span className="absolute top-2 left-2 px-2 py-0.5 text-xs bg-accent-500 text-background-50 rounded-md">
            新
          </span>
        )}
        {article.isHot && (
          <span className="absolute top-2 right-2 px-2 py-0.5 text-xs bg-primary-500 text-background-50 rounded-md">
            热
          </span>
        )}
      </div>
      <div className="p-3 md:p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-xs text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">{article.category}</span>
        </div>
        <h3 className="text-sm md:text-base font-medium text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1.5 line-clamp-1">{article.excerpt}</p>
        <div className="flex items-center gap-3 mt-2.5 text-xs text-foreground-500">
          <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{formatViews(article.views)}</span>
          <span className="flex items-center gap-1"><i className="ri-heart-line"></i>{formatViews(article.likes)}</span>
          <span className="ml-auto">{article.publishDate}</span>
        </div>
      </div>
    </Link>
  );
}